import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  ListItemIcon,
  MenuItem,
  Paper,
  Select,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import HistoryIcon from "@mui/icons-material/History";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BlockIcon from "@mui/icons-material/Block";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";
import Navbar from "../../Navbar";
import ConciliacionBancariaDialog from "./ConciliacionBancariaDialog";
import ModalAnularOrdenPago from "./ModalAnularOrdenPago";
import ModalConfirmarOrdenPago from "./ModalConfirmarOrdenPago";
import ModalHistorialNotificacionesOrdenPago from "./ModalHistorialNotificacionesOrdenPago";
import {
  anularLoteOrdenPago,
  anularOrdenPago,
  descargarDocumentoOrdenPago,
  getOrdenesPago,
  reenviarMailOrdenPago,
} from "./ApiServices";
import {
  ESTADOS_LISTADO_MIXTO_FILTRO,
  cantidadPagosNoProveedorLote,
  colorEstadoLote,
  colorEstadoOp,
  esFilaLoteListado,
  esLoteAnulable,
  esLoteSoloPagosVarios,
  esOpAccionesOperativas,
  esOpAnulable,
  esOpPendienteAprobacion,
  etiquetaEstadoLote,
  etiquetaEstadoOp,
  etiquetaMedioPagoApi,
  etiquetaTipoMedio,
  formatCuit,
  formatMoneda,
  guardarBlobComoArchivo,
  idFilaListadoMixta,
  itemsListadoOrdenPago,
  mapFilaListadoMixta,
  mensajeErrorAnularOp,
  opPerteneceALote,
  obtenerLoteDeOrdenPago,
} from "./utils";

const PAGE_SIZE = 50;

const formatearFecha = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY") : "—";
};

const importeOrden = (totales) => {
  if (!totales) return null;
  if (totales.neto != null) return Number(totales.neto);
  if (totales.importe_centavos != null) {
    return Number(totales.importe_centavos) / 100;
  }
  return null;
};

const etiquetaMedioPagoFila = (row) => {
  if (esFilaLoteListado(row)) return "—";
  if (Array.isArray(row?.medios_pago) && row.medios_pago.length) {
    return etiquetaMedioPagoApi(row.medios_pago);
  }
  if (row?.medio_pago) {
    return etiquetaMedioPagoApi(row.medio_pago);
  }
  if (row?.tipo_medio) {
    return etiquetaTipoMedio(row.tipo_medio, row);
  }
  return "—";
};

const obtenerNotificacionOrden = (orden) =>
  orden?.notificacion ?? orden?.notificacion_proveedor ?? null;

const formatearFechaHora = (value) => {
  if (!value) return "";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "";
};

const filtrosIniciales = () => ({
  cuit: "",
  fecha_desde: "",
  fecha_hasta: "",
  nro_op: "",
  nro_lote: "",
  estado: "",
});

const resumenProveedoresLote = (proveedores = []) => {
  const nombres = proveedores
    .map((p) => p.razon_social || formatCuit(p.cuit || p.nro_cuit))
    .filter(Boolean);
  return {
    texto: nombres.slice(0, 2).join(" · ") || "—",
    extra: Math.max(0, nombres.length - 2),
    title: nombres.join(" · "),
  };
};

export default function ListadoOrdenesPago() {
  const navigate = useNavigate();
  const [filtros, setFiltros] = useState(filtrosIniciales);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosIniciales);
  const [page, setPage] = useState(0);
  const [filas, setFilas] = useState([]);
  const [paginacion, setPaginacion] = useState({
    total: 0,
    limit: PAGE_SIZE,
    offset: 0,
    hay_mas: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogConciliacion, setDialogConciliacion] = useState(false);
  const [historialOrden, setHistorialOrden] = useState(null);
  const [reenvioOrden, setReenvioOrden] = useState(null);
  const [reenviando, setReenviando] = useState(false);
  const [descargandoId, setDescargandoId] = useState(null);
  const [anularOrden, setAnularOrden] = useState(null);
  const [anularLote, setAnularLote] = useState(null);
  const [anulando, setAnulando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getOrdenesPago({
        ...filtrosAplicados,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });

      const items = itemsListadoOrdenPago(data).map(mapFilaListadoMixta);
      setFilas(items);
      setPaginacion({
        total: Number(data.paginacion?.total ?? items.length),
        limit: Number(data.paginacion?.limit ?? PAGE_SIZE),
        offset: Number(data.paginacion?.offset ?? page * PAGE_SIZE),
        hay_mas: Boolean(data.paginacion?.hay_mas),
      });
    } catch (err) {
      setError(err.message || "Error al cargar órdenes de pago");
      setFilas([]);
    } finally {
      setLoading(false);
    }
  }, [filtrosAplicados, page]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const aplicarFiltros = (e) => {
    e?.preventDefault?.();
    setPage(0);
    setFiltrosAplicados({ ...filtros });
  };

  const limpiarFiltros = () => {
    const vacio = filtrosIniciales();
    setFiltros(vacio);
    setFiltrosAplicados(vacio);
    setPage(0);
  };

  const abrirFila = useCallback(
    (fila) => {
      if (!fila?.id) return;
      if (esFilaLoteListado(fila)) {
        navigate(`/lotes_orden_pago/${fila.id}`);
        return;
      }
      navigate(`/ordenes_pago_proveedores/${fila.id}`);
    },
    [navigate]
  );

  const abrirAprobacion = useCallback(
    (orden, event) => {
      event?.stopPropagation?.();
      if (!orden?.id || esFilaLoteListado(orden) || opPerteneceALote(orden)) {
        return;
      }
      if (!esOpPendienteAprobacion(orden.estado)) return;
      navigate(`/ordenes_pago_proveedores/${orden.id}/editar`);
    },
    [navigate]
  );

  const abrirHistorial = useCallback((orden, event) => {
    event?.stopPropagation?.();
    if (!orden?.id || esFilaLoteListado(orden)) return;
    setHistorialOrden(orden);
  }, []);

  const abrirReenvio = useCallback((orden, event) => {
    event?.stopPropagation?.();
    if (!orden?.id || esFilaLoteListado(orden)) return;
    if (!esOpAccionesOperativas(orden.estado)) return;
    setReenvioOrden(orden);
  }, []);

  const abrirAnularOp = useCallback((orden, event) => {
    event?.stopPropagation?.();
    if (!orden?.id || esFilaLoteListado(orden) || opPerteneceALote(orden)) {
      return;
    }
    if (!esOpAnulable(orden.estado)) return;
    setAnularOrden(orden);
  }, []);

  const abrirAnularLote = useCallback((lote, event) => {
    event?.stopPropagation?.();
    if (!lote?.id || !esFilaLoteListado(lote)) return;
    if (!esLoteAnulable(lote.estado)) return;
    setAnularLote(lote);
  }, []);

  const handleAnularOrden = useCallback(
    async (motivo) => {
      if (!anularOrden?.id) return;
      setAnulando(true);
      try {
        const data = await anularOrdenPago(anularOrden.id, {
          user: localStorage.getItem("user"),
          motivo,
        });
        setAnularOrden(null);

        if (data?.ya_anulada) {
          await swal({
            title: "Ya estaba anulada",
            text: data?.msg || "La orden de pago ya se encontraba anulada.",
            icon: "info",
            closeOnClickOutside: false,
          });
        } else {
          await swal({
            title: "Orden anulada",
            text:
              data?.msg ||
              "Se liberaron las facturas y quedaron disponibles otra vez.",
            icon: "success",
            closeOnClickOutside: false,
          });
        }

        await cargar();
      } catch (err) {
        swal({
          title: "No se pudo anular",
          text: mensajeErrorAnularOp(err),
          icon: "error",
          closeOnClickOutside: false,
        });
      } finally {
        setAnulando(false);
      }
    },
    [anularOrden, cargar]
  );

  const handleAnularLote = useCallback(
    async (motivo) => {
      if (!anularLote?.id) return;
      setAnulando(true);
      try {
        const data = await anularLoteOrdenPago(anularLote.id, {
          user: localStorage.getItem("user"),
          motivo,
        });
        setAnularLote(null);
        await swal({
          title: "Lote anulado",
          text:
            data?.msg ||
            "Se anularon las órdenes del lote y los comprobantes quedaron disponibles otra vez.",
          icon: "success",
          closeOnClickOutside: false,
        });
        await cargar();
      } catch (err) {
        swal({
          title: "No se pudo anular el lote",
          text: err.message || "No se pudo anular el lote",
          icon: "error",
          closeOnClickOutside: false,
        });
      } finally {
        setAnulando(false);
      }
    },
    [anularLote, cargar]
  );

  const descargarTodoZip = useCallback(async (orden, event) => {
    event?.stopPropagation?.();
    if (!orden?.id || esFilaLoteListado(orden)) return;

    setDescargandoId(orden.id);
    try {
      const { blob, filename } = await descargarDocumentoOrdenPago(orden.id, {
        tipo: "completo",
      });
      guardarBlobComoArchivo(blob, filename);
    } catch (err) {
      swal({
        title: "Error al descargar",
        text: err.message || "No se pudo descargar el documento",
        icon: "error",
        closeOnClickOutside: false,
      });
    } finally {
      setDescargandoId(null);
    }
  }, []);

  const cerrarReenvio = useCallback(() => {
    if (reenviando) return;
    setReenvioOrden(null);
  }, [reenviando]);

  const handleReenviarCorreo = useCallback(
    async (email) => {
      if (!reenvioOrden?.id) return;

      setReenviando(true);
      try {
        const data = await reenviarMailOrdenPago(reenvioOrden.id, {
          email,
          user: localStorage.getItem("user"),
        });
        const notif = data?.notificacion ?? data;
        const enviado = Boolean(notif?.mail_enviado);

        await swal({
          title: enviado ? "Correo reenviado" : "No se pudo enviar el correo",
          text: enviado
            ? `La documentación fue enviada a ${notif.destinatario || email}.`
            : "No se pudo completar el reenvío del correo.",
          icon: enviado ? "success" : "warning",
          closeOnClickOutside: false,
        });

        setReenvioOrden(null);
        cargar();
      } catch (err) {
        swal({
          title: "Error al reenviar",
          text: err.message || "No se pudo reenviar el correo",
          icon: "error",
          closeOnClickOutside: false,
        });
      } finally {
        setReenviando(false);
      }
    },
    [reenvioOrden, cargar]
  );

  const columns = useMemo(
    () => [
      {
        id: "documento",
        header: "Documento",
        size: 105,
        minSize: 90,
        maxSize: 120,
        accessorFn: (row) =>
          esFilaLoteListado(row)
            ? row.nro_lote || ""
            : row.nro_op || "",
        Cell: ({ row }) => {
          const fila = row.original;
          if (esFilaLoteListado(fila)) {
            const cantOp =
              fila.cantidad_ordenes || fila.ordenes_pago?.length || 0;
            const cantPnp = cantidadPagosNoProveedorLote(fila);
            let subtitulo = `${cantOp} OP`;
            if (cantPnp > 0 && cantOp > 0) {
              subtitulo = `${cantOp} OP · ${cantPnp} pagos varios`;
            } else if (cantPnp > 0) {
              subtitulo = `${cantPnp} pago${cantPnp === 1 ? "" : "s"} varios`;
            }
            return (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <LayersOutlinedIcon fontSize="small" color="primary" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {fila.nro_lote ?? `Lote #${fila.id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {subtitulo}
                  </Typography>
                </Box>
              </Stack>
            );
          }
          return (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {fila.nro_op ?? "—"}
              </Typography>
              {fila._enLote && fila.lote?.nro_lote ? (
                <Typography variant="caption" color="text.secondary" noWrap>
                  Lote {fila.lote.nro_lote}
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
      {
        id: "fecha",
        header: "Fecha",
        size: 88,
        minSize: 80,
        maxSize: 95,
        accessorFn: (row) => row.fecha_pago ?? row.fecha,
        Cell: ({ cell }) => formatearFecha(cell.getValue()),
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 140,
        minSize: 120,
        maxSize: 155,
        Cell: ({ cell, row }) => {
          const estado = cell.getValue();
          const fila = row.original;
          if (esFilaLoteListado(fila)) {
            return (
              <Chip
                label={etiquetaEstadoLote(estado, fila.estado_label)}
                size="small"
                color={colorEstadoLote(estado)}
                variant="outlined"
              />
            );
          }
          return (
            <Chip
              label={etiquetaEstadoOp(estado, fila.estado_label)}
              size="small"
              color={colorEstadoOp(estado)}
              variant="outlined"
            />
          );
        },
      },
      {
        id: "concepto",
        header: "Concepto",
        size: 95,
        maxSize: 110,
        minSize: 80,
        accessorFn: (row) =>
          esFilaLoteListado(row)
            ? esLoteSoloPagosVarios(row)
              ? "pagos_varios"
              : "lote"
            : row.concepto || "pago",
        Cell: ({ row }) => {
          const fila = row.original;
          if (esFilaLoteListado(fila)) {
            if (esLoteSoloPagosVarios(fila)) {
              return (
                <Chip
                  label="Pagos varios"
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              );
            }
            if (cantidadPagosNoProveedorLote(fila) > 0) {
              return (
                <Chip
                  label="Lote + varios"
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              );
            }
            return (
              <Chip label="Lote" size="small" color="primary" variant="outlined" />
            );
          }
          const concepto = String(fila.concepto || "pago").toLowerCase();
          const esAnticipo = concepto === "anticipo";
          return (
            <Box sx={{ minWidth: 0 }}>
              <Chip
                label={esAnticipo ? "Adelanto" : "Pago"}
                size="small"
                color={esAnticipo ? "info" : "default"}
                variant={esAnticipo ? "filled" : "outlined"}
              />
              {esAnticipo && fila.motivo ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  noWrap
                  title={fila.motivo}
                  sx={{ mt: 0.25, maxWidth: 90 }}
                >
                  {fila.motivo}
                </Typography>
              ) : null}
            </Box>
          );
        },
      },
      {
        id: "proveedor",
        header: "Proveedor",
        size: 155,
        minSize: 120,
        maxSize: 180,
        accessorFn: (row) =>
          esFilaLoteListado(row)
            ? esLoteSoloPagosVarios(row)
              ? "Pagos varios / Impuestos y conceptos"
              : (row.proveedores || [])
                  .map((p) => p.razon_social)
                  .filter(Boolean)
                  .join(" · ")
            : row.proveedor?.razon_social || "",
        Cell: ({ row }) => {
          const fila = row.original;
          if (esFilaLoteListado(fila)) {
            if (esLoteSoloPagosVarios(fila)) {
              return (
                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                  Pagos varios / Impuestos y conceptos
                </Typography>
              );
            }
            const resumen = resumenProveedoresLote(fila.proveedores);
            const cantPnp = cantidadPagosNoProveedorLote(fila);
            return (
              <Tooltip title={resumen.title || "—"} arrow>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                    {resumen.texto}
                  </Typography>
                  {resumen.extra > 0 || cantPnp > 0 ? (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {resumen.extra > 0 ? `+${resumen.extra} más` : ""}
                      {resumen.extra > 0 && cantPnp > 0 ? " · " : ""}
                      {cantPnp > 0
                        ? `+${cantPnp} pago${cantPnp === 1 ? "" : "s"} varios`
                        : ""}
                    </Typography>
                  ) : null}
                </Box>
              </Tooltip>
            );
          }
          return (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                title={fila.proveedor?.razon_social}
              >
                {fila.proveedor?.razon_social || "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {formatCuit(fila.proveedor?.nro_cuit)}
              </Typography>
            </Box>
          );
        },
      },
      {
        id: "empresa",
        header: "Empresa",
        size: 75,
        minSize: 60,
        maxSize: 90,
        accessorFn: (row) => row.empresa?.nombre ?? "—",
      },
      {
        id: "neto",
        header: "Neto",
        size: 105,
        minSize: 90,
        maxSize: 120,
        muiTableBodyCellProps: { align: "right" },
        muiTableHeadCellProps: { align: "right" },
        accessorFn: (row) => importeOrden(row.totales),
        Cell: ({ cell, row }) => {
          const v = cell.getValue();
          return (
            <Typography
              variant="body2"
              fontWeight={esFilaLoteListado(row.original) ? 700 : 600}
              noWrap
            >
              {v != null ? formatMoneda(v) : "—"}
            </Typography>
          );
        },
      },
      {
        id: "medio_pago",
        header: "Medio",
        size: 100,
        minSize: 80,
        maxSize: 120,
        accessorFn: (row) => etiquetaMedioPagoFila(row),
      },
      {
        id: "notificado",
        header: "Notif.",
        size: 72,
        minSize: 60,
        maxSize: 85,
        accessorFn: (row) =>
          esFilaLoteListado(row)
            ? null
            : obtenerNotificacionOrden(row)?.mail_enviado,
        Cell: ({ row }) => {
          if (esFilaLoteListado(row.original)) return "—";
          const notif = obtenerNotificacionOrden(row.original);
          if (!notif) {
            return (
              <Tooltip title="Sin registro de notificación" arrow>
                <Chip label="N/D" size="small" variant="outlined" />
              </Tooltip>
            );
          }

          const fecha = formatearFechaHora(
            notif.fecha_envio ?? notif.created_at
          );
          const tooltip = notif.mail_enviado
            ? [
                notif.destinatario ? `A: ${notif.destinatario}` : null,
                fecha ? `Enviado: ${fecha}` : null,
                notif.usuario ? `Por: ${notif.usuario}` : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : "No se envió el correo";

          return (
            <Tooltip title={tooltip} arrow>
              <Chip
                label={notif.mail_enviado ? "Sí" : "No"}
                size="small"
                color={notif.mail_enviado ? "success" : "warning"}
                variant={notif.mail_enviado ? "filled" : "outlined"}
              />
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  const menuAcciones = useCallback(
    ({ closeMenu, row }) => {
      const fila = row.original;

      if (esFilaLoteListado(fila)) {
        return [
          <MenuItem
            key="revisar-lote"
            onClick={() => {
              abrirFila(fila);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <VisibilityOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Revisar lote
          </MenuItem>,
          esLoteAnulable(fila.estado) ? (
            <MenuItem
              key="anular-lote"
              onClick={(e) => {
                abrirAnularLote(fila, e);
                closeMenu();
              }}
            >
              <ListItemIcon>
                <BlockIcon fontSize="small" color="error" />
              </ListItemIcon>
              Anular lote
            </MenuItem>
          ) : null,
        ].filter(Boolean);
      }

      return [
        opPerteneceALote(fila) ? (
          <MenuItem
            key="ver-lote"
            onClick={() => {
              const lote = obtenerLoteDeOrdenPago(fila);
              if (lote?.id) navigate(`/lotes_orden_pago/${lote.id}`);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <VisibilityOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Revisar lote
          </MenuItem>
        ) : null,
        !opPerteneceALote(fila) && esOpPendienteAprobacion(fila.estado) ? (
          <MenuItem
            key="editar-op"
            onClick={(e) => {
              abrirAprobacion(fila, e);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Editar
          </MenuItem>
        ) : null,
        !opPerteneceALote(fila) && esOpAnulable(fila.estado) ? (
          <MenuItem
            key="anular-op"
            onClick={(e) => {
              abrirAnularOp(fila, e);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <BlockIcon fontSize="small" color="error" />
            </ListItemIcon>
            Anular
          </MenuItem>
        ) : null,
        <MenuItem
          key="descargar-zip"
          disabled={descargandoId === fila.id}
          onClick={(e) => {
            descargarTodoZip(fila, e);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          Descargar todo (ZIP)
        </MenuItem>,
        <MenuItem
          key="historial-envios"
          onClick={(e) => {
            abrirHistorial(fila, e);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          Historial de envíos
        </MenuItem>,
        esOpAccionesOperativas(fila.estado) ? (
          <MenuItem
            key="reenviar-correo"
            onClick={(e) => {
              abrirReenvio(fila, e);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <MailOutlineIcon fontSize="small" />
            </ListItemIcon>
            Reenviar correo
          </MenuItem>
        ) : null,
      ].filter(Boolean);
    },
    [
      navigate,
      abrirFila,
      abrirAnularLote,
      abrirAprobacion,
      abrirAnularOp,
      descargarTodoZip,
      descargandoId,
      abrirHistorial,
      abrirReenvio,
    ]
  );

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ pt: "20px", pb: 4, px: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Órdenes de pago
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Listado unificado: expandí un lote para ver sus OP, o abrí una OP
              suelta.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => setDialogConciliacion(true)}
          >
            Importar conciliación
          </Button>
        </Stack>

        <Paper
          component="form"
          onSubmit={aplicarFiltros}
          elevation={1}
          sx={{ p: 2, mb: 2, backgroundColor: "#fff" }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <FilterListIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filtros
            </Typography>
          </Stack>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="CUIT proveedor"
                value={filtros.cuit}
                onChange={(e) =>
                  setFiltros((prev) => ({ ...prev, cuit: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="N° OP"
                value={filtros.nro_op}
                onChange={(e) =>
                  setFiltros((prev) => ({ ...prev, nro_op: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="N° lote"
                value={filtros.nro_lote}
                onChange={(e) =>
                  setFiltros((prev) => ({ ...prev, nro_lote: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Desde"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filtros.fecha_desde}
                onChange={(e) =>
                  setFiltros((prev) => ({
                    ...prev,
                    fecha_desde: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Hasta"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={filtros.fecha_hasta}
                onChange={(e) =>
                  setFiltros((prev) => ({
                    ...prev,
                    fecha_hasta: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="filtro-estado-op">Estado</InputLabel>
                <Select
                  labelId="filtro-estado-op"
                  label="Estado"
                  value={filtros.estado}
                  onChange={(e) =>
                    setFiltros((prev) => ({ ...prev, estado: e.target.value }))
                  }
                >
                  <MenuItem value="">Todos</MenuItem>
                  {ESTADOS_LISTADO_MIXTO_FILTRO.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" size="small">
                  Buscar
                </Button>
                <Button variant="outlined" size="small" onClick={limpiarFiltros}>
                  Limpiar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={cargar}>
                Reintentar
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <MaterialReactTable
              columns={columns}
              data={filas}
              getRowId={(originalRow) => idFilaListadoMixta(originalRow)}
              localization={MRT_Localization_ES}
              layoutMode="grid"
              enableColumnActions={false}
              enableColumnFilters={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableHiding={false}
              enablePagination={false}
              enableExpanding
              enableExpandAll={false}
              getSubRows={(originalRow) =>
                esFilaLoteListado(originalRow)
                  ? originalRow.ordenes_pago || []
                  : undefined
              }
              getRowCanExpand={(row) =>
                esFilaLoteListado(row.original) &&
                (row.original.ordenes_pago?.length ?? 0) > 0
              }
              enableRowActions
              positionActionsColumn="last"
              displayColumnDefOptions={{
                "mrt-row-expand": {
                  header: "Exp.",
                  size: 40,
                  minSize: 36,
                  maxSize: 44,
                  muiTableBodyCellProps: {
                    onClick: (event) => event.stopPropagation(),
                  },
                },
                "mrt-row-actions": {
                  header: "Acc.",
                  size: 48,
                  minSize: 44,
                  maxSize: 56,
                  muiTableBodyCellProps: {
                    onClick: (event) => event.stopPropagation(),
                  },
                },
              }}
              initialState={{ density: "compact" }}
              muiTableBodyRowProps={({ row }) => ({
                onClick: (event) => {
                  if (
                    event.target.closest(
                      "button, a, .MuiIconButton-root, .MuiCheckbox-root"
                    )
                  ) {
                    return;
                  }
                  abrirFila(row.original);
                },
                sx: {
                  cursor: row.original?.id ? "pointer" : "default",
                  backgroundColor: row.depth > 0 ? "rgba(36,113,163,0.04)" : undefined,
                },
              })}
              renderRowActionMenuItems={menuAcciones}
              muiTableHeadCellProps={{
                sx: {
                  fontSize: 13,
                  color: "white",
                  fontWeight: "normal",
                  backgroundColor: "#2471A3",
                  borderColor: "black",
                  whiteSpace: "nowrap",
                  px: 1,
                },
              }}
              muiTableBodyCellProps={{
                sx: { borderColor: "black", px: 1 },
              }}
              muiTablePaperProps={{
                elevation: 0,
                sx: {
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                },
              }}
              muiTableContainerProps={{
                sx: { maxWidth: "100%" },
              }}
              muiTableProps={{
                sx: { tableLayout: "fixed", width: "100%" },
              }}
              renderTopToolbarCustomActions={() => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {paginacion.total > 0
                      ? `${paginacion.total} registro${
                          paginacion.total === 1 ? "" : "s"
                        }`
                      : "Sin resultados"}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={cargar}
                  >
                    Actualizar
                  </Button>
                </Stack>
              )}
              renderEmptyRowsFallback={() => (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 3, textAlign: "center" }}
                >
                  No hay órdenes de pago ni lotes con los filtros seleccionados.
                </Typography>
              )}
            />

            <TablePagination
              component="div"
              count={paginacion.total}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          </>
        )}
      </Container>

      <ConciliacionBancariaDialog
        open={dialogConciliacion}
        onClose={() => setDialogConciliacion(false)}
        onConciliacionAplicada={() => {
          if (page === 0) cargar();
          else setPage(0);
        }}
      />

      <ModalHistorialNotificacionesOrdenPago
        open={Boolean(historialOrden)}
        onClose={() => setHistorialOrden(null)}
        idOrdenPago={historialOrden?.id}
        nroOp={historialOrden?.nro_op}
      />

      <ModalConfirmarOrdenPago
        open={Boolean(reenvioOrden)}
        onClose={cerrarReenvio}
        onConfirm={handleReenviarCorreo}
        modo="reenviar"
        cuit={reenvioOrden?.proveedor?.nro_cuit}
        razonSocial={reenvioOrden?.proveedor?.razon_social || ""}
        nroOp={reenvioOrden?.nro_op || ""}
        emailInicial={
          obtenerNotificacionOrden(reenvioOrden)?.destinatario || ""
        }
        generando={reenviando}
      />

      <ModalAnularOrdenPago
        open={Boolean(anularOrden)}
        onClose={() => {
          if (!anulando) setAnularOrden(null);
        }}
        onConfirm={handleAnularOrden}
        nroOp={anularOrden?.nro_op || ""}
        anulando={anulando}
      />

      <ModalAnularOrdenPago
        open={Boolean(anularLote)}
        onClose={() => {
          if (!anulando) setAnularLote(null);
        }}
        onConfirm={handleAnularLote}
        nroOp={anularLote?.nro_lote || ""}
        anulando={anulando}
        titulo="Anular lote"
        advertencia="Se anulan todas las órdenes de pago del lote y los comprobantes vuelven a quedar disponibles. ¿Continuar?"
        detalle="Esta acción no se puede deshacer. El lote pasará a estado Anulado."
      />
    </>
  );
}
